import {
  Modal,
  ModalVariant,
  FormSelect,
  FormSelectOption,
  ActionGroup,
  FormAlert,
  Alert,
  FormGroup,
  Form,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { displayLabelIfExists } from '@app/domain/instrument/instrument.helpers'
import type { IInstrument } from '@app/domain/instrument/instrument.interface'
import type { IVisitDefinition } from '@app/domain/visit/visit.interface'
import { hasContent } from '@app/lib/string'
import { ValidationStatusId } from '@app/lib/validators'
import { SmallButton } from '@app/utils/styles/small-button'

interface Props {
  addInstrumentToVisit: (instrumentId: string) => void
  definition: IVisitDefinition
  isModalOpen: boolean
  instruments: IInstrument[]
  onCancel: () => void
}

export const AddInstrumentModal = function ({
  addInstrumentToVisit,
  definition,
  isModalOpen,
  instruments,
  onCancel,
}: Props) {
  const defaultSelectType = 'Please Choose'

  const availableForms = useMemo(() => {
    const { allInstruments, allWorkflow } = definition

    const workflowForms = allWorkflow
      .map((s) => allInstruments.filter((i) => i.id === s.instrumentId))
      .filter((t) => t.length > 0)
      .map((it) => it[0])

    const addedForms = allInstruments.filter((i) => {
      return !workflowForms.includes(i)
    })

    const orderedForms = workflowForms.concat(addedForms)

    const availableForms = instruments.filter((el) => {
      return !orderedForms.some((f) => {
        return f.id === el.id
      })
    })

    return availableForms
  }, [definition, instruments])

  const [isFormDirty, setIsFormDirty] = useState(false)
  const [formSelectValue, setFormSelectValue] = useState<string>(undefined)
  const [formSelectValidation, setFormSelectValidation] = useState<
    ValidationStatusId
  >(ValidationStatusId.Default)

  const isFormValid = useMemo(() => {
    if (isFormDirty) {
      setFormSelectValidation(
        hasContent(formSelectValue) && formSelectValue !== defaultSelectType
          ? ValidationStatusId.Default
          : ValidationStatusId.Error,
      )
    }
    return hasContent(formSelectValue)
  }, [isFormDirty, formSelectValue])

  const onSubmit = useCallback(() => {
    setIsFormDirty(true)
    if (isFormValid) {
      addInstrumentToVisit(formSelectValue)
    }
  }, [addInstrumentToVisit, formSelectValue, isFormValid])

  const onChange = useCallback(
    (value: string) => {
      setFormSelectValue(value)
    },
    [setFormSelectValue],
  )

  return (
    <Modal
      variant={ModalVariant.small}
      title="Select an instrument to add:"
      isOpen={isModalOpen}
      onClose={onCancel}
    >
      {availableForms.length > 0 ? (
        <Form>
          <FormGroup
            fieldId="instrument"
            validated={formSelectValidation}
            helperTextInvalid="Please select an instrument."
          >
            <FormSelect
              value={formSelectValue}
              onChange={onChange}
              aria-label="FormSelect Input"
              onBlur={() => setIsFormDirty(true)}
            >
              <FormSelectOption
                key={0}
                value={null}
                label={defaultSelectType}
              />
              {availableForms.map((option, index) => (
                <FormSelectOption
                  key={index}
                  value={option.id}
                  label={displayLabelIfExists(option)}
                />
              ))}
            </FormSelect>
          </FormGroup>
        </Form>
      ) : (
        <FormAlert>
          <Alert
            variant="info"
            title={'All available instruments have been added to the visit.'}
            aria-live="polite"
            isInline
          />
          <br />
        </FormAlert>
      )}

      <ActionGroup style={{ float: 'right', marginTop: 10 }}>
        <SmallButton
          style={{ marginRight: 5 }}
          key="cancel"
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </SmallButton>

        {availableForms.length > 0 ? (
          <SmallButton key="confirm" variant="primary" onClick={onSubmit}>
            Add
          </SmallButton>
        ) : null}
      </ActionGroup>
    </Modal>
  )
}
